import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { AddFlowContributorDto } from '@/types/dtos/flows/request/add-flow-contributor.dto';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';

export const useAddFlowContributor = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (addFlowContributorDto: AddFlowContributorDto) => {
      const token = await getToken();
      return addFlowContributor(addFlowContributorDto, token ?? '');
    },
    onSuccess: (data, variables) => {
      // Get the event ID from the returned flow data
      const eventId = data?.parent?.id;

      return Promise.all(
        [
          // Only invalidate queries for the specific event if we can identify it
          eventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY, eventId] }),
          queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, variables.flowId] }),
          eventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] }),

          // Fallback: if we can't identify the event, invalidate broadly (but this should be rare)
          !eventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
          !eventId && queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY, variables.flowId] }),
          !eventId && queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY] }),
          invalidatePublicationStateQueries(queryClient),
        ].filter(Boolean),
      );
    },
  });
};

const addFlowContributor = async (addFlowContributorDto: AddFlowContributorDto, token: string): Promise<FlowDto> => {
  const { flowId, ...details } = addFlowContributorDto;
  const url = `${import.meta.env.VITE_API_URL}/flows/${flowId}/contributors`;
  return sendJSONRequest({ url, method: METHOD.POST, body: details, token });
};

export default useAddFlowContributor;
